/**
 * Browser detection & redirect helpers focused on iOS webviews.
 */

const IOS_IN_APP_MARKERS = [
  "fban",
  "fbav",
  "instagram",
  "line",
  "gsa", // Google app
  "snapchat",
  "pinterest",
  "twitter",
  "crystal", // some content blockers expose webviews
  "linkedinapp",
  "messenger",
  "whatsapp",
];

/**
 * Detects if the current device is an Apple mobile device.
 */
export function isAppleMobileDevice() {
  if (typeof window === "undefined" || !window.navigator) {
    return false;
  }

  const ua = window.navigator.userAgent.toLowerCase();
  const platform = (window.navigator.platform || "").toLowerCase();
  const maxTouchPoints = window.navigator.maxTouchPoints || 0;

  const isIPhoneOrIPod = ua.includes("iphone") || ua.includes("ipod");
  const isIPad =
    ua.includes("ipad") || (platform === "macintel" && maxTouchPoints > 1);

  return isIPhoneOrIPod || isIPad;
}

function isStandaloneBrowser(ua) {
  const hasStandaloneFlag =
    typeof window !== "undefined" &&
    window.navigator &&
    window.navigator.standalone;
  if (hasStandaloneFlag) {
    return true;
  }

  if (
    ua.includes("crios") ||
    ua.includes("fxios") ||
    ua.includes("edgios") ||
    ua.includes("focus")
  ) {
    return true;
  }

  const isSafari =
    ua.includes("safari") &&
    !ua.includes("crios") &&
    !ua.includes("fxios") &&
    !ua.includes("edgios") &&
    !ua.includes("chrome");

  return isSafari;
}

/**
 * Checks if we are inside an iOS in-app webview (LinkedIn, X, FB, etc).
 */
export function isIOSInAppBrowser() {
  if (typeof window === "undefined" || !window.navigator) {
    return false;
  }

  const ua = window.navigator.userAgent.toLowerCase();

  if (!isAppleMobileDevice()) {
    return false;
  }

  const hasMarker = IOS_IN_APP_MARKERS.some((marker) => ua.includes(marker));

  return hasMarker || !isStandaloneBrowser(ua);
}

/**
 * Legacy helper kept for backward compatibility.
 */
export function isLinkedInIOSBrowser() {
  if (typeof window === "undefined" || !window.navigator) {
    return false;
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  const hasLinkedInApp = userAgent.includes("linkedinapp");
  return hasLinkedInApp && isAppleMobileDevice();
}

/**
 * Determines if we should offer to open the site in an external browser.
 */
export function shouldOfferBrowserRedirect() {
  return isIOSInAppBrowser();
}

/**
 * Gets the current page URL for redirects.
 */
export function getCurrentURL() {
  if (typeof window === "undefined") {
    return "";
  }
  return window.location.href;
}

function normaliseUrl(url) {
  if (!url) {
    return "";
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  if (typeof window === "undefined") {
    return url;
  }
  return `${window.location.origin}${url}`;
}

/**
 * Attempts to open the provided URL in the user's preferred browser.
 */
export function openInPreferredBrowser(url = null) {
  if (typeof window === "undefined" || !window.navigator) {
    return;
  }

  const targetUrl = normaliseUrl(url || getCurrentURL());
  const ua = window.navigator.userAgent.toLowerCase();
  const attempts = [];

  if (ua.includes("crios")) {
    const chromeUrl = `googlechrome://${targetUrl.replace(
      /^https?:\/\//i,
      ""
    )}`;
    attempts.push(() => window.location.assign(chromeUrl));
  }

  if (ua.includes("fxios")) {
    attempts.push(() =>
      window.location.assign(
        `firefox://open-url?url=${encodeURIComponent(targetUrl)}`
      )
    );
  }

  if (ua.includes("edgios")) {
    attempts.push(() => window.location.assign(`microsoft-edge:${targetUrl}`));
  }

  attempts.push(() => {
    const opened = window.open(targetUrl, "_blank", "noopener");
    if (!opened) {
      window.location.href = targetUrl;
    }
  });

  let delay = 0;
  attempts.forEach((fn) => {
    setTimeout(() => {
      try {
        fn();
      } catch (error) {
        // Ignore failed attempt and continue with the next strategy.
      }
    }, delay);
    delay += 200;
  });
}

/**
 * Backwards-compatible helper; delegates to openInPreferredBrowser.
 */
export function openInSafari(url = null) {
  openInPreferredBrowser(url);
}
